import { Shield } from "lucide-react";
import { PracticeTypeData } from "@/utils/practiceTypeData";
import HeroSection from "@/components/layout/HeroSection";
import { useImagePreload } from "@/hooks/useImagePreload";
import { motion } from "framer-motion";
import { ScrollAnimatedSection } from "@/components/shared/ScrollAnimatedSection";
import hausarztImage from "@/assets/hausarzt-practice-new.jpg";
import zahnarztImage from "@/assets/zahnarzt-practice.jpg";
import mvzImage from "@/assets/mvz-modern-center.jpg";
import klinikImage from "@/assets/klinik-hospital-new.jpg";
import facharztImage from "@/assets/facharzt-practice-new.jpg";

interface PracticeTypeHeroProps {
  practiceType: PracticeTypeData;
}

const practiceImages: Record<string, string> = {
  "hausarzt": hausarztImage,
  "zahnarzt": zahnarztImage,
  "mvz": mvzImage,
  "klinik": klinikImage,
  "facharzt": facharztImage,
};

const practiceAltTexts: Record<string, string> = {
  "hausarzt": "Hausärztin arbeitet konzentriert am Laptop in moderner Praxis – Cyberversicherung schützt sensible Patientendaten vor digitalen Bedrohungen",
  "zahnarzt": "Moderner Zahnarzt-Behandlungsraum mit digitaler Ausstattung – Cyberversicherung sichert Röntgendaten und Praxissoftware",
  "mvz": "Empfangsbereich eines Medizinischen Versorgungszentrums mit vernetzten Arbeitsplätzen – Cyberversicherung für komplexe IT-Infrastrukturen",
  "klinik": "Medizinischer Fachpersonal bedient hochmodernes Dialysegerät in Klinik – Cyberversicherung schützt vernetzte Medizintechnik",
  "facharzt": "Stethoskop auf Laptop-Tastatur symbolisiert digitale Medizin – Cyberversicherung für Facharztpraxen gegen Cyberangriffe"
};

const PracticeTypeHero = ({ practiceType }: PracticeTypeHeroProps) => {
  const heroImage = practiceImages[practiceType.slug];
  
  useImagePreload(heroImage);
  
  return (
    <HeroSection
      image={heroImage}
      alt={practiceAltTexts[practiceType.slug] || `${practiceType.name} mit moderner IT-Ausstattung - Cyberversicherung schützt vor digitalen Bedrohungen und Datenverlust`}
      title={practiceType.heroTitle}
      gradientPreset="none"
      imageOpacity={40}
      overlayColor="background"
      imagePosition={
        practiceType.slug === "hausarzt" ? "center 40%" : 
        practiceType.slug === "facharzt" ? "center 55%" : 
        practiceType.slug === "klinik" ? "center 45%" :
        "center center"
      }
      className="py-20 sm:py-24 lg:py-32 px-4"
    >
      <ScrollAnimatedSection>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-accent/10 rounded-full mb-6"
          >
            <Shield className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-accent" />
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-xl xs:text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6"
          >
            Cyberversicherung für {practiceType.name} – Maßgeschneiderter Schutz vor digitalen Bedrohungen
          </motion.h1>
          
          <div className="space-y-6 mb-6">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-base sm:text-lg lg:text-xl text-foreground max-w-2xl mx-auto font-semibold"
            >
              {practiceType.heroSubtitle}
            </motion.p>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 border border-accent/40 rounded-full"
            >
              <Shield className="w-4 h-4 text-accent" />
              <span className="text-accent font-semibold text-sm">
                {practiceType.practiceCount} {practiceType.name} in Deutschland
              </span>
            </motion.div>
          </div>
        </div>
      </ScrollAnimatedSection>
    </HeroSection>
  );
};

export default PracticeTypeHero;
